import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { authApi, authMfaApi, ApiError, readToken, writeToken } from '@solara/api-client';

/**
 * Admin Console session.
 *
 * Backed by the Solara Core `/auth/signin` endpoint via @solara/api-client.
 * Demo platform admins seeded by `services/core/prisma/seed.ts`:
 *  - admin@solara.ae       / Budapest1928!  (PlatformAdmin)
 *  - compliance@solara.ae  / Budapest1928!  (ComplianceOfficer)
 *  - ops@solara.ae         / Budapest1928!  (OperationsViewer)
 */

const STORAGE_KEY = 'solara.admin.session.v1';

export const DEMO_USERNAME = 'admin@solara.ae';
export const DEMO_PASSWORD = 'Budapest1928!';

export interface SessionState {
  isAuthenticated: boolean;
  signedInAt: string | null;
  username: string | null;
}

export type SignInResult =
  | { ok: true }
  | { ok: false; reason: 'required' | 'invalid' | 'wrong-portal' }
  | { ok: false; reason: 'mfa-required'; mfaChallengeToken: string };

export interface SessionApi extends SessionState {
  signIn: (username: string, password: string) => Promise<SignInResult>;
  /** Complete an MFA challenge with the 6-digit TOTP (or backup code). */
  completeMfa: (mfaChallengeToken: string, token: string) => Promise<{ ok: true } | { ok: false; reason: 'invalid' }>;
  signOut: () => void;
}

const Ctx = createContext<SessionApi | null>(null);

function loadFromStorage(): SessionState {
  if (typeof window === 'undefined') {
    return { isAuthenticated: false, signedInAt: null, username: null };
  }
  const token = readToken();
  if (!token) return { isAuthenticated: false, signedInAt: null, username: null };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SessionState>;
      if (parsed?.username) {
        return {
          isAuthenticated: true,
          signedInAt: parsed.signedInAt ?? new Date().toISOString(),
          username: parsed.username,
        };
      }
    }
  } catch {
    /* ignore */
  }
  return { isAuthenticated: true, signedInAt: new Date().toISOString(), username: null };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(() => loadFromStorage());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (state.isAuthenticated) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [state]);

  // Mount-side portal-mismatch check. If the stored token belongs to a customer
  // user (left over from the shared-token era), boot them back to /login so the
  // session can't silently bombard the admin endpoints with 401s.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    let cancelled = false;
    authApi.me()
      .then((u) => {
        if (cancelled) return;
        if (u.portal === 'CUSTOMER') {
          writeToken(null);
          setState({ isAuthenticated: false, signedInAt: null, username: null });
        }
      })
      .catch(() => {
        /* token was invalid — interceptor will have cleared it */
      });
    return () => { cancelled = true; };
  }, [state.isAuthenticated]);

  const signIn = useCallback<SessionApi['signIn']>(async (username, password) => {
    const u = username.trim();
    if (!u || !password) return { ok: false, reason: 'required' };
    try {
      const res = await authApi.signIn({ email: u, password });
      // MFA-enabled accounts return an empty accessToken + a short-lived
      // challenge token; the caller must complete /auth/mfa/verify.
      if (!res.accessToken && res.mfaChallengeToken) {
        return { ok: false, reason: 'mfa-required', mfaChallengeToken: res.mfaChallengeToken };
      }
      // Reject customer-portal users on the admin console — point them at /5174.
      if (res.user.portal === 'CUSTOMER') {
        return { ok: false, reason: 'wrong-portal' };
      }
      writeToken(res.accessToken);
      setState({
        isAuthenticated: true,
        signedInAt: new Date().toISOString(),
        username: res.user.email,
      });
      return { ok: true };
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 401 || apiErr?.status === 403) return { ok: false, reason: 'invalid' };
      return { ok: false, reason: 'invalid' };
    }
  }, []);

  const completeMfa = useCallback<SessionApi['completeMfa']>(async (mfaChallengeToken, token) => {
    try {
      const res = await authMfaApi.verifyChallenge(mfaChallengeToken, token);
      writeToken(res.accessToken);
      setState({
        isAuthenticated: true,
        signedInAt: new Date().toISOString(),
        username: res.user.email,
      });
      return { ok: true };
    } catch {
      return { ok: false, reason: 'invalid' };
    }
  }, []);

  const signOut = useCallback(() => {
    writeToken(null);
    setState({ isAuthenticated: false, signedInAt: null, username: null });
  }, []);

  const value = useMemo<SessionApi>(
    () => ({ ...state, signIn, completeMfa, signOut }),
    [state, signIn, completeMfa, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
